// src/screens/admin/productView/ProductView.jsx

import { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit2,
  Trash2,
  Package,
  AlertCircle,
  FlaskConical,
  FileText,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import EditProduct from "../../../subscreen/admin/editProduct/EditProduct";
import DeleteConfirmation from "../../../subscreen/admin/deleteConfirmation/DeleteConfirmation";
import "../../../assets/css/admin/adminproductview/AdminProductView.css";
import axios from "axios";
import AppContext from "../../../context/AppContext";

// ─────────────────────────────────────────────────────────────
// IMAGE GALLERY COMPONENT
// ─────────────────────────────────────────────────────────────
const ProductGallery = ({ images }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [imgError, setImgError] = useState(false);
  const { baseUrlForFile } = useContext(AppContext);

  if (!images.length) {
    return (
      <div className="pv-gallery">
        <div className="pv-gallery__main pv-product-img--fallback">
          <Package size={56} />
        </div>
      </div>
    );
  }

  const prev = () => {
    setImgError(false);
    setActiveIdx((i) => (i - 1 + images.length) % images.length);
  };
  const next = () => {
    setImgError(false);
    setActiveIdx((i) => (i + 1) % images.length);
  };

  return (
    <div className="pv-gallery">
      {/* Main image */}
      <div className="pv-gallery__main-wrap">
        {imgError ? (
          <div className="pv-gallery__main pv-product-img--fallback">
            <Package size={48} />
          </div>
        ) : (
          <img
            className="pv-gallery__main"
            src={`${baseUrlForFile}${images[activeIdx]?.image_path}`}
            alt={`Product view ${activeIdx + 1}`}
            onError={() => setImgError(true)}
          />
        )}

        {/* Prev / Next arrows — only if more than 1 image */}
        {images.length > 1 && (
          <>
            <button
              className="pv-gallery__arrow pv-gallery__arrow--prev"
              onClick={prev}
              aria-label="Previous image"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              className="pv-gallery__arrow pv-gallery__arrow--next"
              onClick={next}
              aria-label="Next image"
            >
              <ChevronRight size={16} />
            </button>
            <span className="pv-gallery__counter">
              {activeIdx + 1}/{images.length}
            </span>
          </>
        )}
      </div>

      {/* Thumbnails row — only if more than 1 image */}
      {images.length > 1 && (
        <div className="pv-gallery__thumbs">
          {images.map((img, idx) => (
            <button
              key={idx}
              className={`pv-gallery__thumb ${idx === activeIdx ? "active" : ""}`}
              onClick={() => {
                setActiveIdx(idx);
                setImgError(false);
              }}
              aria-label={`View image ${idx + 1}`}
            >
              <img
                src={`${baseUrlForFile}${img.image_path}`}
                alt={`Thumbnail ${idx + 1}`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────
const AdminProductView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { baseUrl, authToken } = useContext(AppContext);
  const [product, setProduct] = useState();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const viewProductDetails = (id) => {
    axios
      .get(`${baseUrl}/admin/products/viewProductDetails`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        params: { id: id },
      })
      .then((response) => {
        console.log(response.data.singleProduct);
        setProduct(response.data.singleProduct);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    viewProductDetails(id);
  }, [id]);

  if (!product) {
    return (
      <div className="pv-wrapper">
        <div className="pv-not-found">
          <AlertCircle size={56} className="pv-not-found__icon" />
          <h2>Product Not Found</h2>
          <p>This product doesn't exist or has been removed.</p>
          <button
            className="pv-back-btn"
            onClick={() => navigate("/admin/product-list")}
          >
            <ArrowLeft size={16} /> Back to Products
          </button>
        </div>
      </div>
    );
  }

  const images = product.productimages || [];

  const handleEdit = (updatedProduct) => {
    setProduct((prev) => ({ ...prev, ...updatedProduct }));
  };

  const handleDeleteConfirm = () => {
    setIsDeleting(true);
    setTimeout(() => {
      setIsDeleting(false);
      navigate("/admin/product-list");
    }, 1000);
  };

  // keyFeatures is stored as a comma-separated string — split into a clean list for display
  const keyFeaturesList = (product.keyFeatures || "")
    .split(",")
    .map((f) => f.trim())
    .filter(Boolean);

  return (
    <div className="pv-wrapper">
      {/* ── Top bar ── */}
      <div className="pv-topbar">
        <button
          className="pv-back-btn"
          onClick={() => navigate("/admin/product-list")}
        >
          <ArrowLeft size={16} /> Back to Products
        </button>
        <div className="pv-topbar__actions">
          <button
            className="pv-btn pv-btn--edit"
            onClick={() => setIsEditOpen(true)}
          >
            <Edit2 size={15} /> Edit Product
          </button>
          <button
            className="pv-btn pv-btn--delete"
            onClick={() => {
              setIsDeleteOpen(true);
            }}
          >
            <Trash2 size={15} /> Delete
          </button>
        </div>
      </div>

      {/* ── Main grid ── */}
      <div className="pv-grid">
        {/* ── Left column ── */}
        <div className="pv-col-left">
          {/* Hero card */}
          <div className="pv-card pv-hero">
            {/* Gallery replaces single image */}
            <ProductGallery images={images} />

            <div className="pv-hero__info">
              <div>
                <h1 className="pv-product-title">{product.name}</h1>
                <p className="pv-product-sub">
                  Size: {product.size}&nbsp;&nbsp;·&nbsp;&nbsp;ID: #{product.id}
                </p>
              </div>
              <div className="pv-badge-grp pv-badge-grp--row">
                <span className="pv-badge pv-badge--cat">
                  {product.adcategory?.name}
                </span>
              </div>
              <div className="pv-price-box">
                <span className="pv-price-label">Price</span>
                <span className="pv-price-value">
                  PKR {Number(product.price).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Description & Key Features card */}
          <div className="pv-card">
            <div className="pv-card-header">
              <FileText size={16} />
              <h3>Description</h3>
            </div>
            <p className="pv-description-text">
              {product.description || "N/A"}
            </p>

            {keyFeaturesList.length > 0 && (
              <div className="pv-features">
                <p className="pv-features-title">Key Features</p>
                <ul className="pv-features-list">
                  {keyFeaturesList.map((feature, i) => (
                    <li key={i} className="pv-features-item">
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Recipe card */}
          {product.productmaterials?.length > 0 && (
            <div className="pv-card pv-recipe-summary-card">
              <div className="pv-card-header">
                <FlaskConical size={16} />
                <h3>Product Materials</h3>
              </div>
              <div className="pv-recipe-summary-list">
                <div className="pv-recipe-sum-row pv-recipe-sum-row--head">
                  <span>Material</span>
                  <span>Qty / Unit</span>
                </div>
                {product.productmaterials.map((item, i) => (
                  <div key={i} className="pv-recipe-sum-row">
                    <span className="pv-recipe-sum-name">
                      {item.admaterial?.name}
                    </span>
                    <span className="pv-recipe-sum-qty">
                      {item.qty} {item.admaterial?.unit}
                    </span>
                  </div>
                ))}
              </div>
              <p className="pv-recipe-sum-total">
                {product?.productmaterials?.length} material
                {product?.productmaterials?.length !== 1 ? "s" : ""} · per unit
                produced
              </p>
            </div>
          )}
        </div>

        {/* ── Right column ── */}
        <div className="pv-col-right">
          {/* Product Details */}
          <div className="pv-card">
            <div className="pv-card-header">
              <Package size={16} />
              <h3>Product Details</h3>
            </div>
            <div className="pv-details">
              {[
                { k: "Product ID", v: `#${product.id}` },
                { k: "Category", v: product.adcategory?.name || "N/A" },
                { k: "Size", v: product.size || "N/A" },
                {
                  k: "Price",
                  v: `PKR ${Number(product.price).toLocaleString()}`,
                },
                { k: "Color", v: product.color || "N/A" },
                {
                  k: "Dimensions",
                  v: product.dimensions || "N/A",
                },
                { k: "Material", v: product.material || "N/A" },
                {
                  k: "Images",
                  v: `${images.length} photo${images.length !== 1 ? "s" : ""}`,
                },
              ].map(({ k, v }) => (
                <div key={k} className="pv-detail-row">
                  <span className="pv-detail-key">{k}</span>
                  <span className="pv-detail-val">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {isEditOpen && (
        <EditProduct
          isOpen
          onClose={() => setIsEditOpen(false)}
          product={product}
          onEdit={handleEdit}
        />
      )}
      {isDeleteOpen && (
        <DeleteConfirmation
          isOpen
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={handleDeleteConfirm}
          productName={product.name}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
};

export default AdminProductView;
